import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionChip } from './pills';
import { useListFetch } from './useListFetch';
import { useWriteFeedback } from './useWriteFeedback';

interface StageRow {
  id: string;
  name: string;
  order: number;
  probability: number | null;
  retiredAt: string | null;
  openDealCount: number;
}

/**
 * PipelineSettings — B4's settings surface (04-Stage §7.3: "a Settings surface, not a CRM
 * screen"; design 03 §B4). The tenant's pipeline vocabulary: add · inline rename · up/down
 * reorder (explicitly NOT drag — a settings screen reached on a phone, §7.3) · retire
 * (reversible ⇒ UndoToast with the X-5 survival disclosure, never a confirm) · per-stage win
 * probability in [0,1] (tenant-declared, X-4: never defaulted; blank clears).
 * There is NO delete affordance — DN-6 is unruled and retire removes the everyday need.
 */
export const PipelineSettings: React.FC = () => {
  const { t } = useTranslation('common');
  const feedback = useWriteFeedback();
  const list = useListFetch<StageRow[]>('/api/stages?all=1', {});
  const stages = list.data ?? [];

  const [addName, setAddName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [probDrafts, setProbDrafts] = useState<Record<string, string>>({});

  const call = async (url: string, init: RequestInit): Promise<boolean> => {
    const res = await fetch(url, init).catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.saveFailed')); return false; }
    await list.retry(); // refetch — the durable list is the truth, not local mutation
    return true;
  };

  const addStage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addName.trim()) return;
    if (await call('/api/stages', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: addName.trim() }) })) setAddName('');
  };

  const rename = async (stage: StageRow) => {
    const name = renameDraft.trim();
    setRenamingId(null);
    if (!name || name === stage.name) return;
    await call(`/api/stages/${stage.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
  };

  const reorder = async (stage: StageRow, direction: 'up' | 'down') => {
    await call(`/api/stages/${stage.id}/reorder`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ direction }) });
  };

  const retire = async (stage: StageRow) => {
    const res = await fetch(`/api/stages/${stage.id}/retire`, { method: 'POST' }).catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.actionFailed')); return; }
    const body = await res.json().catch(() => ({} as any));
    await list.retry();
    // §7.4/X-5 — disclose what SURVIVED with a real count; at zero the clause disappears rather
    // than reading "0 deals". Retire is reversible ⇒ the toast carries a working Undo.
    const count: number = body.openDealCount ?? 0;
    const message = count > 0
      ? t('settings.pipeline.retiredWithDeals', { name: stage.name, count })
      : t('settings.pipeline.retired', { name: stage.name });
    feedback.undoable(message, () => {
      void fetch(`/api/stages/${stage.id}/unretire`, { method: 'POST' }).then(() => list.retry());
    });
  };

  const unretire = async (stage: StageRow) => {
    await call(`/api/stages/${stage.id}/unretire`, { method: 'POST' });
  };

  const saveProbability = async (stage: StageRow) => {
    const draft = (probDrafts[stage.id] ?? '').trim();
    const probability = draft === '' ? null : Number(draft);
    if (probability !== null && (Number.isNaN(probability) || probability < 0 || probability > 1)) {
      feedback.fail(t('settings.pipeline.probabilityInvalid'));
      return;
    }
    if (await call(`/api/stages/${stage.id}/probability`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ probability }) })) {
      setProbDrafts((d) => { const { [stage.id]: _, ...rest } = d; return rest; });
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-border-neutral p-6 shadow-sm" data-testid="pipeline-settings">
      <h3 className="text-lg font-semibold mb-1 text-black">{t('settings.pipeline.title')}</h3>
      <p className="text-sm text-text-muted mb-4">{t('settings.pipeline.subtitle')}</p>

      {list.status === 'error' && (
        <div className="text-sm text-text-muted py-4 flex items-center gap-3" data-testid="pipeline-settings-error">
          {t('listStates.error')}
          <button type="button" onClick={list.retry} className="min-h-[44px] px-3 rounded-lg border border-border-neutral text-sm font-semibold">{t('listStates.retry')}</button>
        </div>
      )}

      <div className="space-y-2" data-testid="stage-settings-list">
        {stages.map((stage, i) => (
          <div key={stage.id} data-testid="stage-settings-row" className={`flex items-center gap-2 flex-wrap rounded-lg border border-border-neutral px-3 py-2 ${stage.retiredAt ? 'opacity-60' : ''}`}>
            <div className="flex flex-col">
              <button type="button" aria-label={t('settings.pipeline.moveUp', { name: stage.name })} data-testid="stage-move-up" disabled={i === 0 || !!stage.retiredAt}
                onClick={() => void reorder(stage, 'up')} className="text-xs leading-none px-2 py-1 disabled:opacity-30">▲</button>
              <button type="button" aria-label={t('settings.pipeline.moveDown', { name: stage.name })} data-testid="stage-move-down" disabled={i === stages.length - 1 || !!stage.retiredAt}
                onClick={() => void reorder(stage, 'down')} className="text-xs leading-none px-2 py-1 disabled:opacity-30">▼</button>
            </div>
            {renamingId === stage.id ? (
              <input autoFocus data-testid="stage-rename-input" value={renameDraft} onChange={(e) => setRenameDraft(e.target.value)}
                onBlur={() => void rename(stage)} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setRenamingId(null); }}
                className="h-9 px-2 rounded-lg border border-primary text-sm flex-1 min-w-32" dir="auto" />
            ) : (
              <span data-testid="stage-settings-name" className="text-sm font-semibold text-text-strong cursor-text flex-1 min-w-32 truncate"
                onClick={() => { if (!stage.retiredAt) { setRenameDraft(stage.name); setRenamingId(stage.id); } }}>{stage.name}</span>
            )}
            {stage.retiredAt ? (
              <>
                <span data-testid="stage-retired-marker" className="text-xs font-semibold text-text-muted uppercase">{t('settings.pipeline.retiredMarker')}</span>
                <ActionChip testId="stage-unretire" label={t('settings.pipeline.unretire')} onAct={() => void unretire(stage)} />
              </>
            ) : (
              <>
                <label className="text-xs text-text-muted flex items-center gap-1">
                  {t('settings.pipeline.probability')}
                  <input data-testid="stage-probability-input" type="number" min={0} max={1} step={0.05}
                    value={probDrafts[stage.id] ?? (stage.probability ?? '')}
                    onChange={(e) => setProbDrafts((d) => ({ ...d, [stage.id]: e.target.value }))}
                    onBlur={() => void saveProbability(stage)}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    className="h-9 w-20 px-2 rounded-lg border border-border-neutral text-sm" dir="ltr" />
                </label>
                <ActionChip testId="stage-retire" label={t('settings.pipeline.retire')} onAct={() => void retire(stage)} />
              </>
            )}
          </div>
        ))}
        {list.status === 'ready' && stages.length === 0 && (
          <div className="text-sm text-text-muted italic py-2">{t('settings.pipeline.empty')}</div>
        )}
      </div>

      <form onSubmit={addStage} className="flex items-center gap-2 mt-4">
        <input data-testid="stage-add-input" value={addName} onChange={(e) => setAddName(e.target.value)}
          placeholder={t('settings.pipeline.addPlaceholder')} className="h-10 px-3 rounded-lg border border-border-neutral text-sm flex-1" dir="auto" />
        <button type="submit" data-testid="stage-add-submit" disabled={!addName.trim()}
          className="h-10 px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50">{t('settings.pipeline.add')}</button>
      </form>

      {feedback.node}
    </div>
  );
};
